import _ from 'lodash';
import moment from 'moment';

const DefaultNotificationAudio = require('./assets/audio/beep.mp3');

const Stash = {
  state: {
    departments: [],
    store: {},
    orders: [], // for order listings,
    loading: false,
    time: moment(),
    fulfiller: {},
    order_source_settings: [],

    item_groups: [],
    item_group: {
      product: {},
      items: [],
    },

    products: [],

    product: {},
    facets: [],
    preferences: [],
  },

  // Fulfiller, Departments, and Store
  // ---------------------------------------------------------------------------
  setFulfiller(data = {}) {
    const {
      departments = {},
      store = {},
      order_source_settings: orderSourceSettings = {},
      ...fulfiller
    } = data;

    this.state.departments = departments.data || [];
    this.state.store = store || {};
    this.state.order_source_settings = orderSourceSettings.data || [];
    this.state.fulfiller = fulfiller;
  },

  // Orders
  // ---------------------------------------------------------------------------
  // Flush and set a new list of orders
  setOrders(orders = []) {
    this.state.orders = orders;
  },

  hasOrder(id) {
    return this.getOrderIndex(id) >= 0;
  },

  // Fetch an order by id
  getOrder(id) {
    return this.state.orders.find(o => Number(o.id) === Number(id));
  },

  getOrderIndex(id) {
    return this.state.orders.findIndex(o => Number(o.id) === Number(id));
  },

  // add a new order or update an existing one in place
  setOrder(id, data) {
    const idx = this.getOrderIndex(id);

    if (idx >= 0) {
      this.state.orders.splice(idx, 1, data);
    } else {
      this.state.orders.push(data);
    }
  },

  setItemGroup(group) {
    this.state.item_group.product = group.product || {};
    this.state.item_group.items = _.get(group, 'items.data', []);
  },

  // Remove an order by id
  removeOrder(id) {
    this.setOrders(this.state.orders.filter(o => Number(o.id) !== Number(id)));
  },

  // Modify a subset of attributes for an existing order
  updateOrder(id, data) {
    const idx = this.getOrderIndex(id);

    if (idx < 0) {
      return;
    }

    this.state.orders.splice(idx, 1, { ...this.state.orders[idx], ...data });
  },

  /**
   * Attempt to get the URL to an audio file to be used as a custom notification
   * sound for the order based on the order's source attribute. Falls back to a
   * default 'beep'.
   *
   * @param {string} orderSource
   */
  getNotificationAudioForOrderSource(orderSource) {
    if (typeof orderSource !== 'string') {
      return DefaultNotificationAudio;
    }

    const sanitizedOrderSource = new String(orderSource).toLowerCase().trim();
    const sourceSettings =
      this.state.order_source_settings.find(
        settings => settings.source === sanitizedOrderSource
      ) || {};

    return sourceSettings.notification_audio_url || DefaultNotificationAudio;
  },

  getCustomIconImageForOrderSource(orderSource) {
    if (typeof orderSource !== 'string') {
      return null;
    }

    const sanitizedOrderSource = new String(orderSource).toLowerCase().trim();
    const sourceSettings =
      this.state.order_source_settings.find(
        settings => settings.source === sanitizedOrderSource
      ) || {};

    return sourceSettings.image_url || null;
  },

  // Products
  // ---------------------------------------------------------------------------
  // Set a product. This is used when adding a new item to an order
  setProduct(data = {}) {
    this.state.product = _.omit(data, 'facets');
    this.setFacets(_.get(data, 'facets.data', []));
  },

  // Sets the facets from the product being addded to an order. This also
  // sets the default preferences for each facet on the state.
  setFacets(facets = []) {
    this.state.facets = facets;
    this.flushPreferences();

    facets.forEach(f =>
      _.get(f, 'choices.data', [])
        .filter(c => c.default)
        .forEach(c => this.addPreference(f, c))
    );
  },

  // If the facet only allows a single choice, we will make sure the previous
  // one has been removed before adding a new one.
  addPreference(facet, choice) {
    if (facet.choice_limit === 1) {
      this.flushPreferences(facet);
    }

    this.state.preferences.push({
      facet_id: facet.id,
      choice_id: choice.id,
    });
  },

  flushPreferences(facet = null) {
    if (facet === null) {
      this.state.preferences = [];

      return;
    }

    this.state.preferences = this.state.preferences.filter(
      p => p.facet_id !== facet.id
    );
  },

  removePreference(facet, choice) {
    const index = this.state.preferences.findIndex(
      p => p.facet_id === facet.id && p.choice_id === choice.id
    );

    if (index >= 0) {
      this.state.preferences.splice(index, 1);
    }
  },

  // set products for the product search both when adding a new item to and order
  // or when viewing the inventory tab
  setProducts(products = []) {
    this.state.products = products;
  },

  // when a product's stock is altered, various sections of the state need to
  // be updated to reflect this changee.
  updateProduct(id, data) {
    // first walk through the orders, looking at products included on each item
    this.state.orders
      .reduce((acc, order) => acc.concat(_.get(order, 'items.data', [])), [])
      .filter(item => Number(item.product_id) === Number(data.id))
      .forEach(item => Object.assign(item.product, data));

    // then walk through the grouped items, again looking at products included on each item
    this.state.item_groups
      .filter(group => Number(group.product.id) === Number(data.id))
      .forEach(group => (group.product = { ...group.product, ...data }));
  },

  // Items
  // ---------------------------------------------------------------------------
  // add a new item to an existing order
  addItem(orderId, data) {
    const order = this.getOrder(orderId);

    if (!order) {
      return;
    }

    order.items.data = [...order.items.data, data];
  },

  // remove an existing item
  removeItem(orderId, id) {
    let idx;
    const order = this.getOrder(orderId);

    if (order) {
      idx = _.findIndex(order.items.data, { id: +id });

      if (idx >= 0) {
        order.items.data.splice(idx, 1);
        order.item_count--;
      }
    }

    if (!_.has(this.state.product, 'items.data')) {
      return;
    }

    idx = _.findIndex(this.state.product.items.data, { id: +id });

    if (idx >= 0) {
      this.state.product.items.data.splice(idx, 1);
    }
  },

  // alter attributes on an existing item within an order
  updateItem(orderId, id, data) {
    const order = this.getOrder(orderId);

    if (!order) {
      return;
    }

    if (_.has(data, 'processed_at')) {
      order.processed_item_count += data.processed_at ? 1 : -1;
    }

    const idx = _.findIndex(_.get(order, 'items.data', []), { id: +id });

    if (idx < 0) {
      return;
    }

    order.items.data.splice(idx, 1, { ...order.items.data[idx], ...data });
  },

  // set the grouped item details when bulk-processing products
  setItemGroups(items) {
    this.state.item_groups = items;
  },
};

class OrderScope {
  constructor(state) {
    this.state = state;
  }

  incomplete() {
    return this.state.orders.filter(o =>
      ['submitted', 'processing'].includes(o.status.toLowerCase())
    );
  }

  completed() {
    return this.state.orders.filter(o => o.completed_at !== null);
  }

  timeThreshold() {
    return this.state.time.clone().add(1, 'h');
  }

  current() {
    const limit = this.timeThreshold();

    return this.incomplete().filter(
      o => moment.utc(o.pickup_at).local() < limit
    );
  }

  future() {
    const limit = this.timeThreshold();

    return this.incomplete().filter(
      o => moment.utc(o.pickup_at).local() > limit
    );
  }
}

export default Stash;

export const scopedOrders = new OrderScope(Stash.state);



// WEBPACK FOOTER //
// ./src/stash.js